import { Component, OnInit, signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { startWith } from 'rxjs';
import { DatepickerHelper } from '../../../../shared/validators/date-picker-form-validators';
import { setControlDisabled } from '../../../../shared/utils/set-control-disabled';
import { Document } from '@models/documents/document';
import { EntityChangeData } from '@models/documents/entity-change-data';
import { companyNameValidator } from '../../../../shared/validators/compny-name.validator';
import { PaymentMethod, paymentMethodOptions } from '../../../models/company/company-change-name.model';
import { Form410BStepsEnum, Form410BTransaction } from '../../../models/company/form-410b-transaction';
import { IStep } from "@models/ui-models/step";
import { InputComponent } from "@shared/controls/input/input.component";
import { YesNoControlComponent } from "@shared/controls/yes-no-control-component/yes-no-control.component";
import {
  AbnMaskedInputComponent
} from "@shared/controls/masked-input/specific-masked-inputs/abn-masked-input/abn-masked-input.component";
import { RadioComponent } from "@shared/controls/radio/radio.component";
import { TextareaComponent } from "@shared/controls/textarea/textarea.component";
import { abnDeclarationMessage } from "../../../../shared/constants/abn-declaration-message.constant";
import { CheckboxComponent } from "@shared/controls/checkbox/checkbox.component";
import { ValidationErrorComponent } from "@shared/controls/validation-error/validation-error.component";
import { BaseStepperFormComponent, StepperFormComponent, StepperFormDescriptionComponent } from "../../stepper-form";

@Component({
  selector: 'app-form410b',
  standalone: true,
  templateUrl: './form-410b-transaction.component.html',
  imports: [
    ReactiveFormsModule,
    InputComponent,
    RadioComponent,
    YesNoControlComponent,
    TextareaComponent,
    AbnMaskedInputComponent,
    StepperFormDescriptionComponent,
    StepperFormComponent,
    CheckboxComponent,
    ValidationErrorComponent,
  ],
  styleUrl: '../../stepper-form/base-stepper-component/base-stepper-form.component.scss',
})
export class Form410bTransactionComponent extends BaseStepperFormComponent<Form410BStepsEnum, Form410BTransaction> implements OnInit {
  override readonly StepsEnum = Form410BStepsEnum;
  readonly paymentMethodOptions = paymentMethodOptions;
  readonly abnDeclarationMessage = abnDeclarationMessage;

  stepsSignal: WritableSignal<IStep<Form410BStepsEnum>[]> = signal(this.steps);
  override stepperForm = new FormGroup({
    [Form410BStepsEnum.FormDetails]: new FormGroup({}),
    [Form410BStepsEnum.CompanyNameDetails]: new FormGroup({
      proposedCompanyName: new FormControl<string>('', [Validators.required, Validators.minLength(7), companyNameValidator()]),
      isProposedNameIdenticalToRegisteredBusinessName: new FormControl<boolean>(false, [Validators.required]),
      abnConfirmation: new FormControl<boolean | null>(false, [Validators.requiredTrue]),
      businessNameHolderAbn: new FormControl<string>(''),
      paymentMethod: new FormControl<PaymentMethod>(PaymentMethod.INV, [Validators.required]),
    }),
    [Form410BStepsEnum.ManualProcessing]: new FormGroup({
      manualProcessing: new FormControl<boolean>(false, [Validators.required]),
      manualProcessingReason: new FormControl<string>('', [Validators.required])
    }),
  });

  constructor() {
    super();
    this.setupSteps(Form410BStepsEnum);
    this.redirectAfterSubmit = true;
    this.stepsSignal.set(this.steps);
  }

  ngOnInit(): void {
    this.listenIsBusinessNameChanges();
    this.listenManualProcessingChanges();
    this.setupChange();
  }

  override afterSubmit(changes: EntityChangeData[]) {
    this.setupChange(changes[0] as Form410BTransaction);
  }

  override setupChange(change: Form410BTransaction = this.formModel): void {
    this.stepperForm.patchValue(this.stepperForm.value);
    this.companyNameDetailsForm.controls.abnConfirmation.setValue(this.isEdit);

    if (!this.isEdit) {
      return;
    }

    this.stepperForm.patchValue({
      [Form410BStepsEnum.CompanyNameDetails]: {
        ...change,
      },
      [Form410BStepsEnum.ManualProcessing]: {
        ...change,
      }
    });
  }

  override buildDocument(): Document | null {
    const companyNameDetailsFormValue = this.stepperForm.value[Form410BStepsEnum.CompanyNameDetails];
    const manualProcessingFormValue = this.stepperForm.value[Form410BStepsEnum.ManualProcessing];
    const description = companyNameDetailsFormValue?.proposedCompanyName
      ? 'Reserve the new company name as ' + companyNameDetailsFormValue?.proposedCompanyName
      : '';

    const changes = new Form410BTransaction({
      ...companyNameDetailsFormValue as Partial<Form410BTransaction>,
      ...manualProcessingFormValue as Partial<Form410BTransaction>,
      changeDate: DatepickerHelper.buildDateString(DatepickerHelper.getToday()),
      description
    });

    try {
      return new Document({
        changes: [changes],
        entityId: this.companyChangeData.entityId,
        type: 'c:410b',
        documentId: this.companyChangeData?.documentId,
      });
    } catch (error) {
      console.warn(error);
      this.toastr.error('Failed to create Document.', 'Error');
      return null;
    }
  }

  private updateSteps(showManualProcessingStep: boolean): void {
    if (showManualProcessingStep) {
      this.stepsSignal.set(this.steps);
    } else {
      this.stepsSignal.set(this.steps.filter(({ step }) => step !== Form410BStepsEnum.ManualProcessing));
    }
  }

  private listenIsBusinessNameChanges(): void {
    this.companyNameDetailsForm.controls.isProposedNameIdenticalToRegisteredBusinessName.valueChanges
      .pipe(startWith(false), takeUntilDestroyed(this.destroyRef))
      .subscribe((isProposedNameIdenticalToRegisteredBusinessName) => {
        setControlDisabled(this.companyNameDetailsForm.controls.businessNameHolderAbn, !isProposedNameIdenticalToRegisteredBusinessName);
        setControlDisabled(this.companyNameDetailsForm.controls.abnConfirmation, !isProposedNameIdenticalToRegisteredBusinessName);
        this.updateSteps(!!isProposedNameIdenticalToRegisteredBusinessName);
      });
  }

  private listenManualProcessingChanges(): void {
    this.manualProcessingForm.controls.manualProcessing.valueChanges
      .pipe(startWith(false), takeUntilDestroyed(this.destroyRef))
      .subscribe((isManualProcessing) => {
        setControlDisabled(this.manualProcessingForm.controls.manualProcessingReason, !isManualProcessing);
      });
  }

  get companyNameDetailsForm() {
    return this.stepperForm.controls[Form410BStepsEnum.CompanyNameDetails];
  }

  get manualProcessingForm() {
    return this.stepperForm.controls[Form410BStepsEnum.ManualProcessing];
  }
}

